// ============================================================
// RunNowButton — manuell "Synka nu".
// Anropar bolag-autosync-worker:ns POST /sync/run och visar resultat.
// Kräver i .env:
//   VITE_AUTOSYNC_URL = https://bolag-autosync.ekobrott.workers.dev
//   VITE_SYNC_KEY     = <SYNC_TRIGGER_KEY på worker:n>
// Renderar inget om de saknas (tyst tills konfigurerad).
//
// OBS: VITE_SYNC_KEY hamnar i klient-bundeln. Det är en *trigger*-nyckel
// (startar bara en idempotent Dropbox->Firestore-synk, exponerar ingen data),
// och appen är inloggnings-skyddad — så risken är låg.
// ============================================================
import { useState } from 'react'

interface RunResult {
  status?: string
  totalAdded?: number
  totalScanned?: number
  errors?: string[]
  dateWarnings?: string[]
  addedFilenames?: string[]
}

export function RunNowButton() {
  const base = import.meta.env.VITE_AUTOSYNC_URL as string | undefined
  const key = import.meta.env.VITE_SYNC_KEY as string | undefined
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<RunResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  if (!base || !key) return null

  const run = async () => {
    setBusy(true); setError(null); setResult(null)
    try {
      const res = await fetch(base.replace(/\/$/, '') + '/sync/run', {
        method: 'POST',
        headers: { 'X-Sync-Key': key },
      })
      const data = (await res.json()) as RunResult & { error?: string }
      if (!res.ok) setError(data.error || ('HTTP ' + res.status))
      else setResult(data)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="card" style={{ marginBottom: '1rem', padding: '0.75rem 1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
        <button
          onClick={run}
          disabled={busy}
          style={{
            background: busy ? 'var(--border)' : '#3b82f6',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            padding: '0.45rem 0.9rem',
            fontWeight: 600,
            cursor: busy ? 'default' : 'pointer',
          }}
        >
          {busy ? '⏳ Synkar…' : '🔄 Synka nu'}
        </button>
        <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
          Hämtar nya fakturor/kvitton från Dropbox direkt. (Körs annars automatiskt ~06:00 &amp; ~17:00.)
        </span>
      </div>

      {result && (
        <div style={{ marginTop: '0.6rem', fontSize: '0.85rem' }}>
          <span style={{ color: '#10b981', fontWeight: 600 }}>
            ✅ {result.totalAdded ?? 0} nya av {result.totalScanned ?? 0} skannade
          </span>
          {result.errors && result.errors.length > 0 && (
            <span style={{ color: '#ef4444' }}> · {result.errors.length} fel</span>
          )}
          {result.dateWarnings && result.dateWarnings.length > 0 && (
            <div style={{ color: '#f59e0b', marginTop: 4 }}>
              ⚠️ {result.dateWarnings.length} datum-varning(ar): {result.dateWarnings.slice(0, 3).join(' · ')}
            </div>
          )}
          {result.addedFilenames && result.addedFilenames.length > 0 && (
            <div style={{ color: 'var(--text-muted)', marginTop: 4, maxHeight: 120, overflowY: 'auto' }}>
              {result.addedFilenames.map((f) => <div key={f}>+ {f}</div>)}
            </div>
          )}
        </div>
      )}
      {error && (
        <div style={{ marginTop: '0.6rem', fontSize: '0.85rem', color: '#ef4444' }}>Fel: {error}</div>
      )}
    </section>
  )
}

export default RunNowButton
